import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './OrderHistory.css';

function OrderHistory({ user }) {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, [user]);

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/orders`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setOrders(data);
      } else {
        setError('Failed to load orders');
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed to load orders');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pending', class: 'status-pending' },
      processing: { label: 'Processing', class: 'status-processing' },
      shipped: { label: 'Shipped', class: 'status-shipped' },
      delivered: { label: 'Delivered', class: 'status-delivered' },
      cancelled: { label: 'Cancelled', class: 'status-cancelled' },
      payment_failed: { label: 'Payment Failed', class: 'status-failed' }
    };

    const config = statusConfig[status] || { label: status, class: 'status-default' };
    return (
      <span className={`status-badge ${config.class}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="order-history-loading">
        <div className="loading-spinner"></div>
        <p>Loading your orders...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="order-history-error">
        <p>{error}</p>
        <button onClick={fetchOrders} className="btn-retry">
          Try Again
        </button>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="no-orders">
        <div className="no-orders-icon">📦</div>
        <h3>No orders yet</h3>
        <p>Start shopping to see your orders here</p>
        <Link to="/shop" className="cta-button">
          Browse Shop
        </Link>
      </div>
    );
  }

  return (
    <div className="order-history">
      <h2>Order History</h2>
      <div className="orders-list">
        {orders.map((order) => (
          <div key={order.orderId} className="order-card">
            <div className="order-header">
              <div className="order-info">
                <h3>Order #{order.orderId.split('_')[1]}</h3>
                <p className="order-date">Placed on {formatDate(order.createdAt)}</p>
              </div>
              {getStatusBadge(order.status)}
            </div>

            <div className="order-items">
              {order.items && order.items.map((item, index) => (
                <div key={index} className="order-item">
                  <div className="order-item-info">
                    <span className="order-item-name">{item.name}</span>
                    <span className="order-item-qty">Qty: {item.quantity}</span>
                  </div>
                  <span className="order-item-price">
                    ${(parseFloat(item.price?.replace(/[^0-9.-]+/g, '') || 0) * item.quantity).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>

            <div className="order-footer">
              <div className="order-summary-breakdown">
                {order.subtotal != null && (
                  <div className="order-summary-row">
                    <span>Subtotal:</span>
                    <span>${Number(order.subtotal).toFixed(2)}</span>
                  </div>
                )}
                {order.shippingCost != null && (
                  <div className="order-summary-row">
                    <span>Shipping:</span>
                    <span>${Number(order.shippingCost).toFixed(2)}</span>
                  </div>
                )}
                {order.tax != null && (
                  <div className="order-summary-row">
                    <span>Tax:</span>
                    <span>${Number(order.tax).toFixed(2)}</span>
                  </div>
                )}
              </div>
              <div className="order-total">
                <span>Total:</span>
                <span className="total-amount">${Number(order.totalAmount || 0).toFixed(2)}</span>
              </div>
              {order.trackingNumber && (
                <div className="order-tracking">
                  <span>Tracking:</span>
                  <span className="tracking-number">{order.trackingNumber}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default OrderHistory;


